/**
 * RichTextEditor — editor enriquecido reutilizable (TipTap) para el CMDB.
 *
 * Pensado para campos de descripción de tickets:
 *   · Negrita, cursiva, subrayado, tachado, listas, cita, código y enlaces.
 *   · Pegar (Ctrl+V) o arrastrar imágenes: se suben al servidor como adjunto
 *     del ticket y se insertan en el HTML como `/api/feedback/attachments/<id>/raw`.
 *     Nada de base64, así la descripción no engorda el listado de tickets.
 *
 * Se usa tanto en el modal rápido (`MainLayout`) como en el Feedback Hub.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import api from '../services/api';
import '../styles/tiptap-editor.css';
import './RichTextEditor.css';

const EMPTY_HTML = '<p></p>';

function ToolbarButton({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={active ? 'is-active' : ''}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({
  value = '',
  onChange,
  companyId,
  placeholder = 'Describe el contexto, impacto y resultado esperado…',
  disabled = false,
  minHeight = 180,
  onError,
}) {
  const [uploading, setUploading] = useState(0);
  const fileInputRef = useRef(null);

  // Refs para que los callbacks de TipTap (creados una sola vez) siempre
  // trabajen con las últimas props.
  const companyRef = useRef(companyId);
  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);
  const insertImagesRef = useRef(() => {});
  useEffect(() => {
    companyRef.current = companyId;
    onChangeRef.current = onChange;
    onErrorRef.current = onError;
  }, [companyId, onChange, onError]);

  const uploadImage = useCallback(async (file) => {
    const cid = companyRef.current;
    if (!cid) {
      onErrorRef.current?.('Selecciona una empresa antes de pegar imágenes.');
      return null;
    }
    setUploading((count) => count + 1);
    try {
      const attachment = await api.uploadFeedbackAttachment(file, cid, { kind: 'image' });
      return attachment;
    } catch (err) {
      onErrorRef.current?.(err.message || 'No se pudo subir la imagen.');
      return null;
    } finally {
      setUploading((count) => Math.max(0, count - 1));
    }
  }, []);

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || EMPTY_HTML,
    editorProps: {
      attributes: { spellcheck: 'true', class: 'cmdb-rte-content' },
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'));
        if (!files.length) return false;
        event.preventDefault();
        insertImagesRef.current(files);
        return true;
      },
      handleDrop: (_view, event) => {
        const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith('image/'));
        if (!files.length) return false;
        event.preventDefault();
        insertImagesRef.current(files);
        return true;
      },
    },
    onUpdate: ({ editor: instance }) => {
      onChangeRef.current?.(instance.getHTML(), instance.getText());
    },
  });

  const insertImages = useCallback(
    async (files) => {
      if (!editor) return;
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const attachment = await uploadImage(file);
        if (attachment) {
          editor
            .chain()
            .focus()
            .setImage({ src: attachment.inline_url || attachment.url, alt: attachment.original_name })
            .run();
        }
      }
    },
    [editor, uploadImage]
  );

  useEffect(() => {
    insertImagesRef.current = insertImages;
  }, [insertImages]);

  // Sincroniza el contenido cuando el padre reinicia el formulario.
  useEffect(() => {
    if (!editor) return;
    const normalize = (html) => (html === EMPTY_HTML ? '' : html || '');
    if (normalize(editor.getHTML()) !== normalize(value)) {
      editor.commands.setContent(value || EMPTY_HTML, false);
    }
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  const chooseImage = () => fileInputRef.current?.click();

  const onPickImage = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length) insertImages(files);
  };

  const setLink = () => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace:', previous || 'https://');
    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  if (!editor) {
    return <div className="cmdb-rte cmdb-rte-loading">Cargando editor…</div>;
  }

  return (
    <div className="cmdb-rte">
      <div className="tiptap-editor-wrap cmdb-rte-wrap">
        <div className="tiptap-toolbar">
          <div className="tiptap-toolbar-group">
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              active={editor.isActive('bold')}
              disabled={disabled}
              title="Negrita (Ctrl+B)"
            >
              <b>B</b>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              active={editor.isActive('italic')}
              disabled={disabled}
              title="Cursiva (Ctrl+I)"
            >
              <i>I</i>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              active={editor.isActive('underline')}
              disabled={disabled}
              title="Subrayado (Ctrl+U)"
            >
              <u>U</u>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleStrike().run()}
              active={editor.isActive('strike')}
              disabled={disabled}
              title="Tachado"
            >
              <s>S</s>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleCode().run()}
              active={editor.isActive('code')}
              disabled={disabled}
              title="Código"
            >
              {'</>'}
            </ToolbarButton>
          </div>

          <div className="tiptap-toolbar-group">
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              active={editor.isActive('bulletList')}
              disabled={disabled}
              title="Lista con viñetas"
            >
              •
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              active={editor.isActive('orderedList')}
              disabled={disabled}
              title="Lista numerada"
            >
              1.
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              active={editor.isActive('blockquote')}
              disabled={disabled}
              title="Cita"
            >
              ❝
            </ToolbarButton>
          </div>

          <div className="tiptap-toolbar-group">
            <ToolbarButton onClick={setLink} active={editor.isActive('link')} disabled={disabled} title="Insertar enlace">
              🔗
            </ToolbarButton>
            <ToolbarButton onClick={chooseImage} disabled={disabled || uploading > 0} title="Insertar imagen (también puedes pegarla)">
              🖼
            </ToolbarButton>
          </div>

          <div className="tiptap-toolbar-group">
            <ToolbarButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={disabled || !editor.can().undo()}
              title="Deshacer"
            >
              ↩
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={disabled || !editor.can().redo()}
              title="Rehacer"
            >
              ↪
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
              disabled={disabled}
              title="Quitar formato"
            >
              ⌫
            </ToolbarButton>
          </div>

          <div className="cmdb-rte-hint">
            {uploading > 0 ? 'Subiendo imagen…' : 'Pega imágenes con Ctrl+V'}
          </div>
        </div>

        <div className="cmdb-rte-canvas" style={{ minHeight }}>
          <EditorContent editor={editor} />
        </div>
      </div>

      <input
        type="file"
        accept="image/*"
        multiple
        ref={fileInputRef}
        className="d-none"
        onChange={onPickImage}
        tabIndex={-1}
      />
    </div>
  );
}
